// Complete Admin Panel — root component + Login/Dashboard/Rooms/Gallery inline;
// larger managers extracted into ./admin/* to keep this file small enough
// for the Babel visual-edits plugin to traverse.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Hotel, Lock, LogOut, Home, Eye, EyeOff, X, Loader2,
  BarChart3, Calendar, Users, Image, MessageSquare,
  Plus, Trash2, Edit, Save,
  RefreshCw, Bot, Languages, Utensils, TrendingUp, Gift, Mail,
  Landmark, Search, Image as ImageIcon, Inbox as InboxIcon, History, UserCheck, ShieldCheck, Coffee, CalendarDays } from 'lucide-react';
import { API_URL, apiCall, getToken, setToken, removeToken, parseJson } from './admin/_shared';
import TranslationsManager from './admin/TranslationsManager';
import ReceptionDashboard from './admin/ReceptionDashboard';
import OwnerToday from './admin/OwnerToday';
import HousekeepingDashboard from './admin/HousekeepingDashboard';
import RoomHistory from './admin/RoomHistory';
import ArrivalDesk from './admin/ArrivalDesk';
import FbCovers from './admin/FbCovers';
import OwnerBI from './admin/OwnerBI';
import ReservationStats from './admin/stats/ReservationStats';
import RatesAvailability from './admin/rates/RatesAvailability';
import TrustTab from './admin/TrustTab';
import BarTab from './admin/bar/BarTab';
import BookingsManager from './admin/BookingsManager';
import TestimonialsManager from './admin/TestimonialsManager';
import UserManagement from './admin/UserManagement';
import AttendanceCard from './admin/AttendanceCard';
import ConversationsManager from './admin/ConversationsManager';
import CouponsManager from './admin/CouponsManager';
import InRoomManager from './admin/InRoomManager';
import FinanceDashboard from './admin/FinanceDashboard';
import ShiftLogbook from './admin/ShiftLogbook';
import CommandPalette from './admin/CommandPalette';
import RoomUnitsEditor from './admin/RoomUnitsEditor';
import RoomPhotosManager from './admin/RoomPhotosManager';
import { RoomTranslationsBar, RoomTranslationsEditor } from './admin/RoomTranslations';
import GalleryCaptions from './admin/GalleryCaptions';
import PhotoHealthCard from './admin/PhotoHealthCard';
import ContactInbox from './admin/ContactInbox';
import DashboardNotes from './admin/DashboardNotes';
import GuestMemoryCard from './admin/GuestMemoryCard';
import GuestProfileModal from './admin/GuestProfileModal';
import { GuestAlarm } from './admin/GuestAlarm';
import { ChannelHealthBadge } from './admin/ChannelHealthBadge';
import CalendarDashboard from './admin/CalendarDashboard';
import GuestEmailsManager from './admin/GuestEmailsManager';

// ============ Login Component ============
const LoginForm = ({ onLogin, error, loading }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(username, password);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white text-center">
          <Hotel className="w-12 h-12 mx-auto mb-3" />
          <h1 className="text-2xl font-bold">Hotel La Rosa Negra</h1>
          <p className="text-orange-100 text-sm mt-1">Admin Panel</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter username"
              required
              data-testid="admin-username-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-12"
                placeholder="Enter password"
                required
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="login-error-message">
              <X className="w-4 h-4" />
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-5 h-5" />Sign In</>}
          </button>
        </form>
        
        <div className="px-6 pb-6">
          <Link to="/" className="block text-center text-sm text-gray-500 hover:text-gray-700">← Back to website</Link>
        </div>
      </div>
    </div>
  );
};

// ============ Dashboard Component ============
const LEADERSHIP_ROLES = ['super_admin', 'admin', 'manager'];

// Turn `/api/admin/reception/bookings/abc123/real-name` into a human-friendly
// short label the ticker can render without wrapping. Keep the last two path
// segments, strip UUID-shaped IDs, prettify.
const humanizePath = (path) => {
  if (!path) return '';
  const clean = String(path).replace(/^\/api\/admin\//, '').replace(/^\/api\//, '');
  const parts = clean.split('/').filter(Boolean);
  // Drop leaf IDs that look like ObjectId / UUID / long tokens.
  const tail = parts.slice(-3).filter(p => !/^[0-9a-f-]{20,}$/i.test(p) && p.length < 40);
  return tail.join(' › ');
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const roleChipColor = (role) => {
  switch (role) {
    case 'super_admin':  return 'bg-red-100 text-red-700';
    case 'admin':        return 'bg-blue-100 text-blue-700';
    case 'manager':      return 'bg-purple-100 text-purple-700';
    case 'reception':    return 'bg-green-100 text-green-700';
    case 'housekeeping': return 'bg-orange-100 text-orange-700';
    case 'maintenance':  return 'bg-yellow-100 text-yellow-700';
    case 'marketing':    return 'bg-pink-100 text-pink-700';
    case 'accountant':   return 'bg-teal-100 text-teal-700';
    default:             return 'bg-gray-100 text-gray-700';
  }
};

const LiveAuditFeed = () => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);  // forces relative-time re-render
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await apiCall('/api/admin/audit-log/recent?limit=12');
        if (!alive) return;
        setItems(res.items || []);
        setError(null);
      } catch (err) { if (alive) setError(err.message); }
    };
    load();
    const poll = setInterval(load, 15000);
    const clock = setInterval(() => setTick(t => t + 1), 1000);
    return () => { alive = false; clearInterval(poll); clearInterval(clock); };
  }, []);
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border" data-testid="live-audit-feed">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
        <h3 className="text-lg font-semibold text-gray-900">Live activity</h3>
        <span className="text-xs text-gray-500">who's doing what · updates every 15s</span>
      </div>
      {error && <div className="text-sm text-red-600">Feed unavailable: {error}</div>}
      {!error && items.length === 0 && (
        <div className="text-sm text-gray-500 py-4 text-center">No recent activity yet — actions will appear here as staff work.</div>
      )}
      {items.length > 0 && (
        <ul className="divide-y max-h-80 overflow-y-auto" data-testid="live-audit-feed-list" data-tick={tick}>
          {items.map((it, i) => (
            <li key={`${it.at}-${i}`} className="flex items-center gap-3 py-2 text-sm">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${
                it.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                it.method === 'POST' ? 'bg-green-100 text-green-700' :
                'bg-blue-100 text-blue-700'
              }`}>{it.method}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${roleChipColor(it.role)}`}>
                {it.actor}
              </span>
              <span className="flex-1 text-xs text-gray-700 truncate">{humanizePath(it.path)}</span>
              <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                {relativeTime(it.at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Dashboard = ({ stats, analytics, onRefresh, userRole, currentUser, focus, onOpenCalendar, onOpenTab }) => {
  const showAuditFeed = LEADERSHIP_ROLES.includes(userRole);
  const showLogbook = userRole !== 'marketing';
  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col gap-3 overflow-hidden">
      {['super_admin', 'admin', 'manager'].includes(userRole) && <div className="flex-shrink-0"><OwnerToday /></div>}
      <div className="flex justify-between items-center flex-shrink-0">
        <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
        <button onClick={onRefresh} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {showAuditFeed && <DashboardAlerts onOpenTab={onOpenTab} />}
      {userRole !== 'marketing' && <DashboardNotes onOpenCalendar={onOpenCalendar} />}
      {['super_admin', 'admin', 'manager'].includes(userRole) && <AttendanceCard canManage />}
      {userRole === 'accountant' && <AttendanceCard canManage={false} canExport />}
      {userRole !== 'marketing' && userRole !== 'maintenance' && <GuestMemoryCard userRole={userRole} compact />}

      {/* Stats Cards — tight row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-3 text-white">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-blue-100 text-xs">Total Rooms</p>
              <p className="text-2xl font-bold mt-0.5">{stats?.rooms_count || 0}</p>
            </div>
            <Hotel className="w-6 h-6 text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-3 text-white">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-green-100 text-xs">Gallery Images</p>
              <p className="text-2xl font-bold mt-0.5">{stats?.gallery_count || 0}</p>
            </div>
            <Image className="w-6 h-6 text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-3 text-white">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-purple-100 text-xs">Page Views Today</p>
              <p className="text-2xl font-bold mt-0.5">{stats?.page_views_today || 0}</p>
            </div>
            <BarChart3 className="w-6 h-6 text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-3 text-white">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-orange-100 text-xs">Booking Clicks</p>
              <p className="text-2xl font-bold mt-0.5">{stats?.booking_clicks_today || 0}</p>
            </div>
            <Calendar className="w-6 h-6 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Main content grid: Live activity | Charts | Rooms — fills remaining height */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 flex-1 min-h-0">
        {/* Column 1 — Live activity (leadership) OR Top rooms */}
        {showAuditFeed ? (
          <div className="bg-white rounded-xl p-4 shadow-sm border flex flex-col min-h-0">
            <LiveAuditFeedCompact />
          </div>
        ) : (
          <TopRoomsCard analytics={analytics} />
        )}

        {/* Column 2 — Shift logbook (ops roles) or Page views chart (marketing) */}
        {showLogbook && <ShiftLogbook currentUser={currentUser} highlightId={focus?.logbook_id} />}
        {!showLogbook && analytics && (
          <div className="bg-white rounded-xl p-4 shadow-sm border flex flex-col min-h-0">
            <h3 className="text-sm font-semibold mb-2 text-gray-900">Page Views (Last 14 Days)</h3>
            <div className="flex-1 flex items-end gap-1 min-h-0">
              {analytics.page_views_by_day?.slice(-14).map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center h-full justify-end">
                  <div
                    className="w-full bg-blue-500 rounded-t"
                    style={{ height: `${Math.max(2, (day.count / Math.max(...analytics.page_views_by_day.map(d => d.count), 1)) * 100)}%` }}
                  />
                  <span className="text-[9px] text-gray-500 mt-1 transform -rotate-45 origin-left whitespace-nowrap">
                    {day._id?.slice(5) || ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Column 3 — Top rooms (if leadership; otherwise Rooms Overview) */}
        {showAuditFeed ? <TopRoomsCard analytics={analytics} /> : (
          <RoomsOverviewCompact stats={stats} />
        )}
      </div>

      {/* Bottom strip — Rooms Overview compact (only if we already showed Top Rooms above) */}
      {showAuditFeed && (
        <div className="flex-shrink-0">
          <RoomsOverviewCompact stats={stats} />
        </div>
      )}
    </div>
  );
};

// Ops alerts strip — at-risk arrivals + dirty-room backlog.
// Auto-hides when everything is green so the dashboard stays clean.
const DashboardAlerts = ({ onOpenTab }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await apiCall('/api/admin/ops-alerts/summary');
        if (alive) setData(r);
      } catch (_) { /* silent — dashboard should never break on this */ }
    };
    load();
    const poll = setInterval(load, 30000);
    return () => { alive = false; clearInterval(poll); };
  }, []);
  if (!data || !data.triggered) return null;
  const { dirty_units, at_risk_arrivals, unpaid_departures = [], problem_rooms = [], stale_leads = [] } = data.findings;
  const staleLeadText = stale_leads.slice(0, 3).map((l) => `${l.name} (${l.check_in} → ${l.check_out}, €${Math.round(l.estimate_eur)})`).join('  ·  ');
  const overBacklog = dirty_units >= data.dirty_threshold;
  return (
    <div
      className={`rounded-xl px-4 py-3 shadow-sm border flex-shrink-0 flex flex-col md:flex-row md:items-center gap-3 ${
        (at_risk_arrivals.length > 0 || unpaid_departures.length > 0) ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
      }`}
      data-testid="dashboard-alerts"
    >
      <div className={`flex items-center gap-2 flex-shrink-0 ${(at_risk_arrivals.length > 0 || unpaid_departures.length > 0) ? 'text-red-700' : 'text-amber-700'}`}>
        <span className="text-lg" aria-hidden>⚠</span>
        <span className="text-sm font-bold uppercase tracking-wider">Ops alerts</span>
      </div>
      <div className="flex-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
        {overBacklog && (
          <div className="flex items-center gap-1.5" data-testid="dashboard-alerts-dirty">
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${(at_risk_arrivals.length > 0 || unpaid_departures.length > 0) ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}`}>{dirty_units}</span>
            <span className="text-gray-800">dirty rooms · above threshold ({data.dirty_threshold})</span>
          </div>
        )}
        {unpaid_departures.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid="dashboard-alerts-unpaid">
            <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-500 text-white">{unpaid_departures.length}</span>
            <span className="text-gray-800">{unpaid_departures.length === 1 ? 'departure owes' : 'departures owe'}</span>
            <span className="text-gray-700 truncate max-w-md">
              {unpaid_departures.slice(0, 3).map(u => `${u.guest_name} €${Number(u.balance).toFixed(2)}`).join('  ·  ')}
              {unpaid_departures.length > 3 && `  +${unpaid_departures.length - 3} more`}
            </span>
          </div>
        )}
        {stale_leads.length > 0 && (
          <div className="flex items-start gap-2 text-sm bg-orange-50 border border-orange-200 rounded-lg px-3 py-2" data-testid="dashboard-alert-stale-leads">
            <span className="text-xs font-bold px-2 py-0.5 rounded bg-orange-600 text-white">{stale_leads.length}</span>
            <span className="text-gray-800">{stale_leads.length === 1 ? 'unfinished booking' : 'unfinished bookings'} without a reminder for 24 h+:</span>
            <span className="text-gray-600 truncate flex-1">{staleLeadText}{stale_leads.length > 3 ? `  +${stale_leads.length - 3} more` : ''}</span>
            <button type="button" onClick={() => onOpenTab && onOpenTab('inbox')} className="text-orange-700 font-semibold hover:underline whitespace-nowrap" data-testid="dashboard-alert-stale-leads-link">Inbox →</button>
          </div>
        )}
        {problem_rooms.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid="dashboard-alerts-problem-rooms">
            <span className="text-xs font-bold px-2 py-0.5 rounded bg-violet-600 text-white">{problem_rooms.length}</span>
            <span className="text-gray-800">{problem_rooms.length === 1 ? 'problem room' : 'problem rooms'} (score ≥ {data.problem_threshold}):</span>
            <span className="text-gray-700 truncate max-w-md">
              {problem_rooms.slice(0, 3).map(p => `Room ${p.unit_name} · ${p.score}`).join('  ·  ')}
              {problem_rooms.length > 3 && `  +${problem_rooms.length - 3} more`}
            </span>
            {onOpenTab && <button type="button" onClick={() => onOpenTab('roomhistory')} className="text-xs font-semibold text-violet-700 underline" data-testid="dashboard-alerts-open-room-history">Room History →</button>}
          </div>
        )}
        {at_risk_arrivals.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid="dashboard-alerts-arrivals">
            <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-500 text-white">{at_risk_arrivals.length}</span>
            <span className="text-gray-800">
              {at_risk_arrivals.length === 1 ? 'arrival has no clean room' : 'arrivals have no clean room'}:
            </span>
            <span className="text-gray-700 truncate max-w-md">
              {at_risk_arrivals.slice(0, 3).map(a => `${a.guest_name} → ${a.room_name || a.unit_key}`).join('  ·  ')}
              {at_risk_arrivals.length > 3 && `  +${at_risk_arrivals.length - 3} more`}
            </span>
          </div>
        )}
      </div>
      <Link to="/admin?tab=housekeeping" className="text-xs font-semibold text-orange-600 hover:underline flex-shrink-0">
        Open Housekeeping →
      </Link>
    </div>
  );
};

// Compact version of LiveAuditFeed for the tight dashboard column.
const LiveAuditFeedCompact = () => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await apiCall('/api/admin/audit-log/recent?limit=8');
        if (!alive) return;
        setItems(res.items || []);
        setError(null);
      } catch (err) { if (alive) setError(err.message); }
    };
    load();
    const poll = setInterval(load, 15000);
    const clock = setInterval(() => setTick(t => t + 1), 1000);
    return () => { alive = false; clearInterval(poll); clearInterval(clock); };
  }, []);
  return (
    <>
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
        <h3 className="text-sm font-semibold text-gray-900">Live activity</h3>
      </div>
      {error && <div className="text-xs text-red-600">Feed unavailable</div>}
      {!error && items.length === 0 && (
        <div className="text-xs text-gray-500 py-2">No recent activity yet.</div>
      )}
      {items.length > 0 && (
        <ul className="divide-y flex-1 overflow-y-auto min-h-0" data-testid="live-audit-feed-list" data-tick={tick}>
          {items.map((it, i) => (
            <li key={`${it.at}-${i}`} className="flex items-center gap-2 py-1.5 text-xs">
              <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${
                it.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                it.method === 'POST' ? 'bg-green-100 text-green-700' :
                'bg-blue-100 text-blue-700'
              }`}>{it.method}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${roleChipColor(it.role)}`}>
                {it.actor}
              </span>
              <span className="flex-1 text-[11px] text-gray-700 truncate">{humanizePath(it.path)}</span>
              <span className="text-[9px] text-gray-400 whitespace-nowrap flex-shrink-0">
                {relativeTime(it.at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

const TopRoomsCard = ({ analytics }) => {
  if (!analytics) return <div className="bg-white rounded-xl p-4 shadow-sm border" />;
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border flex flex-col min-h-0">
      <h3 className="text-sm font-semibold mb-2 text-gray-900">Top Viewed Rooms</h3>
      <div className="space-y-2 flex-1 overflow-y-auto min-h-0">
        {analytics.room_views?.slice(0, 5).map((room, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 truncate">{room.room_name}</p>
              <div className="w-full bg-gray-100 rounded-full h-1.5 mt-0.5">
                <div
                  className="bg-orange-500 h-1.5 rounded-full"
                  style={{ width: `${(room.count / Math.max(...analytics.room_views.map(r => r.count), 1)) * 100}%` }}
                />
              </div>
            </div>
            <span className="text-xs text-gray-500 flex-shrink-0">{room.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const RoomsOverviewCompact = ({ stats }) => {
  const rooms = stats?.rooms_summary || [];
  if (!rooms.length) return null;
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border flex flex-col min-h-0">
      <h3 className="text-sm font-semibold mb-2 text-gray-900">Rooms Overview</h3>
      <div className="overflow-x-auto flex-1 min-h-0">
        <table className="w-full text-xs">
          <thead className="text-gray-500">
            <tr className="border-b">
              <th className="text-left py-1.5 px-2 font-medium">Room</th>
              <th className="text-right py-1.5 px-2 font-medium">Price</th>
              <th className="text-right py-1.5 px-2 font-medium">Images</th>
            </tr>
          </thead>
          <tbody>
            {rooms.slice(0, 5).map((r, i) => (
              <tr key={r.id || i} className="border-b last:border-0">
                <td className="py-1 px-2 text-gray-900 truncate max-w-[200px]">{r.name}</td>
                <td className="py-1 px-2 text-right text-gray-700">€{r.price || 0}</td>
                <td className="py-1 px-2 text-right text-gray-700">{r.images ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ Rooms Manager Component ============
const RoomsManager = ({ userRole }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unitsByRoom, setUnitsByRoom] = useState({});
  const [unitsRoom, setUnitsRoom] = useState(null);
  const [photosRoom, setPhotosRoom] = useState(null);
  const [trRoom, setTrRoom] = useState(null);

  const canEdit = ['super_admin', 'admin'].includes(userRole);
  const canPhotos = ['super_admin', 'admin', 'manager', 'marketing'].includes(userRole);
  const canEditUnits = ['super_admin', 'admin', 'manager'].includes(userRole);
  const canTranslate = ['super_admin', 'admin', 'manager', 'marketing'].includes(userRole);

  const fetchUnits = async () => {
    try {
      const d = await apiCall('/api/admin/rooms/units');
      const map = {};
      d.rooms.forEach((r) => { map[r.room_id] = r; });
      setUnitsByRoom(map);
    } catch (err) { console.error('Error fetching units:', err); }
  };

  useEffect(() => {
    fetchRooms();
    fetchUnits();
  }, []);

  const fetchRooms = async () => {
    try {
      const data = await apiCall('/api/rooms');
      setRooms(data);
    } catch (err) {
      console.error('Error fetching rooms:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingRoom) return;
    setSaving(true);
    try {
      await apiCall(`/api/admin/rooms/${editingRoom.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editingRoom.name,
          price: editingRoom.price,
          description: editingRoom.description,
          amenities: editingRoom.amenities
        })
      });
      await fetchRooms();
      setEditingRoom(null);
    } catch (err) {
      alert('Error saving: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Rooms Management</h2>
      {canTranslate && <RoomTranslationsBar onDone={fetchRooms} />}
      <div className="grid gap-4">
        {rooms.map((room) => (
          <div key={room.id} className="bg-white rounded-xl p-4 shadow-sm border">
            {editingRoom?.id === room.id ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Room Name</label>
                    <input
                      type="text"
                      value={editingRoom.name}
                      onChange={(e) => setEditingRoom({ ...editingRoom, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (€/night)</label>
                    <input
                      type="number"
                      value={editingRoom.price}
                      onChange={(e) => setEditingRoom({ ...editingRoom, price: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editingRoom.description}
                    onChange={(e) => setEditingRoom({ ...editingRoom, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg h-24"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                  </button>
                  <button onClick={() => setEditingRoom(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="flex flex-col gap-1.5 flex-shrink-0" data-testid={`room-photos-${room.id}`}>
                  {room.images?.[0]
                    ? <img src={room.images[0]} alt={room.name} className="w-24 h-24 object-cover rounded-lg" />
                    : <div className="w-24 h-24 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-xs">No photo</div>}
                  {(room.images || []).length > 1 && (
                    <div className="flex gap-1">
                      {room.images.slice(1, 4).map((u) => <img key={u} src={u} alt="" className="w-7 h-7 object-cover rounded" />)}
                      {room.images.length > 4 && <span className="w-7 h-7 rounded bg-slate-100 text-[10px] text-slate-600 flex items-center justify-center">+{room.images.length - 4}</span>}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{room.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{room.description?.substring(0, 100)}...</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-lg font-bold text-orange-500">€{room.price}/night</span>
                    <span className="text-sm text-gray-500">{room.images?.length || 0} photos</span>
                    {canPhotos && (
                      <button onClick={() => setPhotosRoom(room)} className="flex items-center gap-1 text-xs font-semibold text-orange-600 hover:underline" data-testid={`room-photos-manage-${room.id}`}>
                        <ImageIcon className="w-3.5 h-3.5" /> Manage photos
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid={`room-units-${room.id}`}>
                    <span className="text-xs text-gray-500">{unitsByRoom[room.id]?.count || 0} room{(unitsByRoom[room.id]?.count || 0) === 1 ? '' : 's'}:</span>
                    {(unitsByRoom[room.id]?.units || []).slice(0, 14).map((u) => (
                      <span key={u.unit_id} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-semibold">{u.label}</span>
                    ))}
                    {(unitsByRoom[room.id]?.units || []).length > 14 && <span className="text-xs text-gray-400">+{unitsByRoom[room.id].units.length - 14} more</span>}
                    {unitsByRoom[room.id]?.locked && <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">custom</span>}
                    {canEditUnits && (
                      <button onClick={() => setUnitsRoom(room)} className="text-xs font-semibold text-orange-600 hover:underline" data-testid={`room-units-edit-${room.id}`}>
                        Edit room numbers
                      </button>
                    )}
                  </div>
                  {canTranslate && (
                    <button onClick={() => setTrRoom(trRoom === room.id ? null : room.id)} className="mt-2 text-xs font-semibold text-slate-700 hover:underline" data-testid={`room-translations-toggle-${room.id}`}>
                      Translations · {Object.values(room.translations || {}).filter((v) => v?.name).length}/6 languages {trRoom === room.id ? '▲' : '▼'}
                    </button>
                  )}
                  {trRoom === room.id && <RoomTranslationsEditor room={room} onSaved={fetchRooms} />}
                </div>
                {canEdit && (
                  <button onClick={() => setEditingRoom(room)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <Edit className="w-5 h-5 text-gray-500" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {unitsRoom && <RoomUnitsEditor room={unitsRoom} onClose={() => setUnitsRoom(null)} onSaved={fetchUnits} />}
      {photosRoom && (
        <RoomPhotosManager
          room={rooms.find((r) => r.id === photosRoom.id) || photosRoom}
          onClose={() => setPhotosRoom(null)}
          onChanged={fetchRooms}
        />
      )}
    </div>
  );
};

// ============ Gallery Manager Component ============
const GalleryManager = ({ userRole }) => {
  const [gallery, setGallery] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [captionsMode, setCaptionsMode] = useState(false);

  const canEdit = ['super_admin', 'admin'].includes(userRole);
  const canCaption = ['super_admin', 'admin', 'manager', 'marketing'].includes(userRole);

  const fetchAll = async () => {
    try {
      const [galleryData, roomsData] = await Promise.all([
        apiCall('/api/admin/gallery'),
        apiCall('/api/rooms'),
      ]);
      setGallery(galleryData.gallery || []);
      setCategories(galleryData.categories || []);
      setRooms(roomsData || []);
    } catch (err) {
      console.error('Error fetching gallery:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await apiCall(`/api/admin/gallery/${id}`, { method: 'DELETE' });
      await fetchAll();
    } catch (err) {
      alert('Error deleting: ' + err.message);
    }
  };

  const filteredGallery = filter === 'all' ? gallery : gallery.filter(item => item.category === filter);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="gallery-manager">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Gallery ({gallery.length} images)</h2>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 border rounded-lg" data-testid="gallery-filter">
            <option value="all">All Categories</option>
            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          {canCaption && (
            <button
              onClick={() => setCaptionsMode((m) => !m)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border font-medium ${captionsMode ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-800 hover:bg-slate-50'}`}
              data-testid="gallery-captions-toggle"
            >
              <Languages className="w-4 h-4" /> Captions & SEO
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              data-testid="upload-photos-btn"
            >
              <Plus className="w-4 h-4" /> Upload Photos
            </button>
          )}
        </div>
      </div>

      {!captionsMode && <PhotoHealthCard canEdit={canEdit} />}

      {captionsMode && <GalleryCaptions filter={filter} />}

      {!captionsMode && <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {filteredGallery.map((item) => (
          <div key={item.id} className="relative group">
            <img src={item.url} alt={item.alt} className="w-full h-32 object-cover rounded-lg" loading="lazy" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
              <span className="text-white text-xs px-2 py-1 bg-black/50 rounded text-center">{item.category}</span>
              {canEdit && (
                <button onClick={() => handleDelete(item.id)} className="p-1 bg-red-500 text-white rounded" data-testid={`delete-image-${item.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>}

      {showUploadModal && (
        <UploadPhotosModal
          rooms={rooms}
          onClose={() => setShowUploadModal(false)}
          onComplete={async () => {
            await fetchAll();
            setShowUploadModal(false);
          }}
        />
      )}
    </div>
  );
};

// ============ Upload Photos Modal ============
const UploadPhotosModal = ({ rooms, onClose, onComplete }) => {
  const [selectedRoomId, setSelectedRoomId] = useState(rooms[0]?.id || '');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '' });
  const [errors, setErrors] = useState([]);

  const handleFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    const valid = list.filter(f => /^image\/(jpeg|png|webp|gif)$/.test(f.type));
    setFiles(valid);
    if (valid.length !== list.length) {
      setErrors([`${list.length - valid.length} file(s) skipped (only JPG/PNG/WEBP/GIF allowed)`]);
    } else {
      setErrors([]);
    }
  };

  const upload = async () => {
    if (!selectedRoomId || files.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length, current: '' });
    setErrors([]);
    const token = getToken();
    const errs = [];

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress({ done: i, total: files.length, current: f.name });
      const fd = new FormData();
      fd.append('file', f);
      try {
        const resp = await fetch(`${API_URL}/api/admin/rooms/${selectedRoomId}/upload-image`, {
          method: 'POST',
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        const data = await resp.json();
        if (!resp.ok) errs.push(`${f.name}: ${data.detail || 'upload failed'}`);
      } catch (e) {
        errs.push(`${f.name}: ${e.message}`);
      }
    }

    setProgress({ done: files.length, total: files.length, current: '' });
    setErrors(errs);
    setUploading(false);

    if (errs.length === 0) {
      await onComplete();
    }
  };

  const room = rooms.find(r => r.id === Number(selectedRoomId));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="upload-photos-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Upload Photos to Room</h3>
            <p className="text-sm text-gray-500 mt-1">
              Photos are added to the selected room and the public gallery automatically.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" disabled={uploading}>
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
            <select
              value={selectedRoomId}
              onChange={(e) => setSelectedRoomId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              disabled={uploading}
              data-testid="upload-room-select"
            >
              {rooms.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name} (currently {r.images?.length || 0} photos)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Choose images</label>
            <label className="block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition" data-testid="upload-dropzone">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={handleFileChange}
                className="hidden"
                disabled={uploading}
                data-testid="upload-file-input"
              />
              <Image className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                Click to select photos <span className="text-gray-400">(JPG/PNG/WEBP, max 10MB each)</span>
              </p>
              {files.length > 0 && (
                <p className="text-sm font-medium text-orange-600 mt-2">{files.length} file(s) selected</p>
              )}
            </label>
          </div>

          {files.length > 0 && (
            <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
              {files.map((f, i) => (
                <div key={`${f.name}-${f.size}-${i}`} className="relative">
                  <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-20 object-cover rounded" />
                  <p className="text-xs text-gray-500 truncate mt-1">{f.name}</p>
                </div>
              ))}
            </div>
          )}

          {uploading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-900">
                Uploading {progress.done + 1} of {progress.total}{progress.current ? ` — ${progress.current}` : ''}...
              </p>
              <div className="w-full bg-blue-100 h-2 rounded-full mt-2 overflow-hidden">
                <div className="bg-blue-500 h-full transition-all" style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }} />
              </div>
            </div>
          )}

          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm space-y-1">
              {errors.map((e, i) => <p key={`${e}-${i}`}>{e}</p>)}
            </div>
          )}

          {room && !uploading && (
            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
              Target room: <strong>{room.name}</strong> · {room.images?.length || 0} existing photo(s)
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={upload}
            disabled={uploading || files.length === 0 || !selectedRoomId}
            className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="upload-confirm-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {uploading ? 'Uploading...' : `Upload ${files.length} photo${files.length === 1 ? '' : 's'}`}
          </button>
          <button onClick={onClose} disabled={uploading} className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50">
            {errors.length > 0 ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ Main Admin Panel ============
const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  // Read initial tab from `?tab=` query param so PWA shortcuts (Housekeeping / Reception)
  // land the user on the right board directly.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const t = q.get('tab');
      const allowed = ['dashboard','reception','calendar','housekeeping','fb','bar','finance','bi','stats','rates','coupons','inroom','roomhistory','arrivals','emails','inbox','bookings','rooms','gallery','testimonials','trust','translations','conversations','users'];
      return t && allowed.includes(t) ? t : 'dashboard';
    } catch { return 'dashboard'; }
  });
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  // Custom sidebar override for the current role. `null` means "no override,
  // use the built-in tabs[].roles filter". Fetched once after login.
  const [roleOverride, setRoleOverride] = useState(null);
  // Global search (Ctrl+K) navigation payload: {tab, booking_id?, finance_tab?, q?, logbook_id?}
  const [focus, setFocus] = useState(null);
  const [memoryGuest, setMemoryGuest] = useState('');
  // Inbox badge: poll unread department messages every 60s (only for roles that can see the Inbox).
  const [inboxUnread, setInboxUnread] = useState(0);
  useEffect(() => {
    if (!currentUser || !['super_admin', 'admin', 'manager', 'marketing', 'reception'].includes(currentUser.role)) return undefined;
    let alive = true;
    const poll = () => apiCall('/api/admin/contact/unread-count').then((d) => { if (alive) setInboxUnread(d.total || 0); }).catch(() => {});
    poll();
    const id = setInterval(poll, 60000);
    const onFocusWin = () => poll();
    window.addEventListener('focus', onFocusWin);
    window.addEventListener('inbox-changed', onFocusWin);
    return () => { alive = false; clearInterval(id); window.removeEventListener('focus', onFocusWin); window.removeEventListener('inbox-changed', onFocusWin); };
  }, [currentUser, activeTab]);
  const handleSearchNavigate = (target) => {
    if (!target) return;
    if (target.memory_guest) { setMemoryGuest(target.memory_guest); return; }
    setFocus(target);
    setActiveTab(target.tab);
  };

  // Register the service worker once (used by cleaners on the mobile Housekeeping PWA).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').catch(() => { /* fail silently */ });
  }, []);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Try to auth via either the httpOnly cookie or the legacy localStorage
    // token — apiCall attaches both. We only skip the call if neither the
    // token nor a plausible cookie could be present (i.e. never signed in
    // on this device).
    try {
      const user = await apiCall('/api/auth/me');
      setCurrentUser(user);
      setIsAuthenticated(true);
      fetchDashboardData();
      // Load per-role sidebar override (falls back to built-in defaults if none exists).
      try {
        const res = await apiCall(`/api/admin/role-sections/for/${user.role}`);
        if (res && Array.isArray(res.sections)) setRoleOverride(res.sections);
      } catch (_) { /* non-fatal */ }
    } catch (err) {
      // Neither cookie nor bearer worked — clear any stale localStorage.
      removeToken();
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [statsData, analyticsData] = await Promise.all([
        apiCall('/api/admin/stats'),
        apiCall('/api/admin/analytics?days=30')
      ]);
      setStats(statsData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  };

  const handleLogin = async (username, password) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const resp = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // credentials: 'include' lets the browser store the httpOnly cookie
        // returned by the backend so subsequent requests are authenticated
        // even after we clear localStorage.
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });
      const data = await parseJson(resp);
      if (!resp.ok) throw new Error(data.detail || 'Invalid credentials');

      // Keep the token in localStorage during the migration window so
      // admin sub-pages that still read `getToken()` keep working. The
      // httpOnly cookie is set by the response above.
      setToken(data.access_token);
      setIsAuthenticated(true);
      checkAuth();
    } catch (err) {
      setAuthError(err.message);
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    // Best-effort backend logout — clears the httpOnly cookie server-side.
    try {
      const csrfHeaders = {};
      // Same double-submit token the apiCall helper attaches.
      const raw = document.cookie.split(';').map((p) => p.trim()).find((p) => p.startsWith('csrf_token='));
      if (raw) csrfHeaders['X-CSRF-Token'] = decodeURIComponent(raw.slice('csrf_token='.length));
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: csrfHeaders,
      });
    } catch { /* offline — cookie will expire on its own */ }
    removeToken();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setStats(null);
    setAnalytics(null);
  };

  // Any authenticated API call answering 401 (expired/invalid session) drops back to the login screen.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onUnauthorized = (e) => {
      removeToken();
      setIsAuthenticated(false);
      setCurrentUser(null);
      setAuthError(e.detail?.message === 'Invalid or expired token' ? 'Your session expired — please sign in again.' : null);
    };
    window.addEventListener('admin-unauthorized', onUnauthorized);
    return () => window.removeEventListener('admin-unauthorized', onUnauthorized);
  }, [isAuthenticated]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} error={authError} loading={authLoading} />;
  }

  const userRole = currentUser?.role || 'admin';

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, roles: ['super_admin', 'admin', 'manager', 'reception', 'housekeeping', 'maintenance', 'marketing', 'accountant', 'bar', 'waiter'] },
    { id: 'reception', label: 'Reception', icon: Users, roles: ['super_admin', 'admin', 'manager', 'reception', 'housekeeping'] },
    { id: 'calendar', label: 'Calendar', icon: Calendar, roles: ['super_admin', 'admin', 'manager', 'reception', 'housekeeping', 'accountant'] },
    { id: 'housekeeping', label: 'Housekeeping', icon: Hotel, roles: ['super_admin', 'admin', 'manager', 'reception', 'housekeeping', 'maintenance'] },
    { id: 'fb', label: 'F&B Covers', icon: Utensils, roles: ['super_admin', 'admin', 'manager', 'reception'] },
    { id: 'bar', label: 'Bar', icon: Coffee, roles: ['super_admin', 'admin', 'manager', 'reception', 'bar', 'waiter', 'accountant'] },
    { id: 'finance', label: 'Finance', icon: Landmark, roles: ['super_admin', 'admin', 'manager', 'reception', 'accountant'] },
    { id: 'inbox', label: 'Inbox', icon: InboxIcon, roles: ['super_admin', 'admin', 'manager', 'marketing', 'reception'] },
    { id: 'bi', label: 'Owner BI', icon: TrendingUp, roles: ['super_admin', 'admin', 'manager', 'accountant'] },
    { id: 'stats', label: 'Reservation Stats', icon: BarChart3, roles: ['super_admin'] },
    { id: 'rates', label: 'Rates & availability', icon: CalendarDays, roles: ['super_admin'] },
    { id: 'coupons', label: 'Coupons', icon: Gift, roles: ['super_admin', 'admin', 'manager', 'marketing'] },
    { id: 'inroom', label: 'In-Room QR', icon: Utensils, roles: ['super_admin', 'admin', 'manager', 'reception', 'housekeeping', 'maintenance'] },
    { id: 'arrivals', label: 'Arrival Desk', icon: UserCheck, roles: ['super_admin', 'admin', 'manager', 'reception', 'housekeeping'] },
    { id: 'roomhistory', label: 'Room History', icon: History, roles: ['super_admin', 'admin', 'manager', 'reception', 'housekeeping', 'maintenance'] },
    { id: 'emails', label: 'Guest Emails', icon: Mail, roles: ['super_admin', 'admin', 'manager', 'accountant'] },
    { id: 'bookings', label: 'Bookings', icon: Calendar, roles: ['super_admin', 'admin', 'manager', 'reception', 'accountant'] },
    { id: 'rooms', label: 'Rooms', icon: Hotel, roles: ['super_admin', 'admin', 'manager', 'marketing'] },
    { id: 'gallery', label: 'Gallery', icon: Image, roles: ['super_admin', 'admin', 'manager', 'marketing'] },
    { id: 'testimonials', label: 'Reviews', icon: MessageSquare, roles: ['super_admin', 'admin', 'manager', 'marketing'] },
    { id: 'trust', label: 'Trust', icon: ShieldCheck, roles: ['super_admin', 'admin', 'manager', 'marketing'] },
    { id: 'translations', label: 'Translations', icon: Languages, roles: ['super_admin', 'admin'] },
    { id: 'conversations', label: 'Conversations', icon: Bot, roles: ['super_admin', 'admin', 'manager', 'reception', 'marketing'] },
    { id: 'users', label: 'Users', icon: Users, roles: ['super_admin'] },
  ].filter(tab => {
    // Built-in role check MUST pass (backend still enforces).
    if (!tab.roles.includes(userRole)) return false;
    // If a super_admin persisted a custom override for this role, honor it.
    // `users` tab is never hidden from super_admin regardless of the override.
    if (roleOverride && !(userRole === 'super_admin' && tab.id === 'users')) {
      return roleOverride.includes(tab.id);
    }
    return true;
  });

  // If the current active tab is not allowed for this role (e.g., after a role change / re-login),
  // snap back to 'dashboard'. Using useEffect would trigger an extra render; set during render is safe here.
  if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    setTimeout(() => setActiveTab('dashboard'), 0);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CommandPalette onNavigate={handleSearchNavigate} />
      {memoryGuest && <GuestProfileModal name={memoryGuest} userRole={userRole} onClose={() => setMemoryGuest('')} />}
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="w-full px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src="/logo.png"
              alt="La Rosa Negra"
              className="w-9 h-9 rounded-lg object-cover shadow flex-shrink-0"
              data-testid="admin-logo"
            />
            <div className="min-w-0">
              <h1 className="text-sm sm:text-lg font-bold text-gray-900 truncate">La Rosa Negra</h1>
              <p className="text-[10px] sm:text-xs text-gray-500">Admin Panel</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <ChannelHealthBadge userRole={userRole} onOpenTab={(t) => setActiveTab(t)} />
            <span className="hidden md:inline text-sm text-gray-600">
              Welcome, <strong>{currentUser?.username}</strong>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                userRole === 'super_admin' ? 'bg-red-100 text-red-700' :
                userRole === 'admin' ? 'bg-blue-100 text-blue-700' :
                userRole === 'manager' ? 'bg-purple-100 text-purple-700' :
                userRole === 'reception' ? 'bg-green-100 text-green-700' :
                userRole === 'housekeeping' ? 'bg-orange-100 text-orange-700' :
                userRole === 'maintenance' ? 'bg-yellow-100 text-yellow-700' :
                userRole === 'marketing' ? 'bg-pink-100 text-pink-700' :
                userRole === 'accountant' ? 'bg-teal-100 text-teal-700' :
                'bg-gray-100 text-gray-700'
              }`}>{userRole}</span>
            </span>
            <button
              onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs sm:text-sm"
              title="Search everything (Ctrl+K)"
              data-testid="global-search-button"
            >
              <Search className="w-4 h-4" /> <span className="hidden sm:inline">Search</span>
              <kbd className="hidden lg:inline text-[10px] text-gray-400 border rounded px-1">⌘K</kbd>
            </button>
            <Link to="/" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs sm:text-sm">
              <Home className="w-4 h-4" /> <span className="hidden sm:inline">Site</span>
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 text-xs sm:text-sm" data-testid="admin-logout-button">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>
      <GuestAlarm userRole={userRole} onOpenTab={(t) => setActiveTab(t)} />

      <div className="w-full px-3 sm:px-6 py-4 sm:py-6">
        {/* Mobile tab select (< md) */}
        <div className="md:hidden mb-4">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full bg-white border rounded-lg px-3 py-2.5 text-sm font-medium shadow-sm"
            data-testid="admin-mobile-tab-select"
          >
            {tabs.map(tab => (
              <option key={tab.id} value={tab.id}>{tab.label}{tab.id === 'inbox' && inboxUnread > 0 ? ` (${inboxUnread} new)` : ''}</option>
            ))}
          </select>
        </div>

        <div className="md:flex md:gap-6">
          {/* Sidebar (md+) */}
          <nav className="hidden md:block md:w-48 md:flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border p-2 sticky top-20">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-orange-50 text-orange-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="flex-1">{tab.label}</span>
                  {tab.id === 'inbox' && inboxUnread > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="inbox-badge">{inboxUnread > 99 ? '99+' : inboxUnread}</span>
                  )}
                </button>
              ))}
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {(() => {
              const allowed = (id) => tabs.find(t => t.id === id) !== undefined;
              return <>
                {activeTab === 'dashboard' && <Dashboard stats={stats} analytics={analytics} onRefresh={fetchDashboardData} userRole={userRole} currentUser={currentUser} focus={focus} onOpenCalendar={() => setActiveTab('calendar')} onOpenTab={(t) => setActiveTab(t)} />}
                {activeTab === 'reception' && allowed('reception') && <ReceptionDashboard userRole={userRole} focusBookingId={focus?.booking_id} onFocusHandled={() => setFocus(null)} onNavigate={handleSearchNavigate} />}
                {activeTab === 'calendar' && allowed('calendar') && <CalendarDashboard userRole={userRole} />}
                {activeTab === 'housekeeping' && allowed('housekeeping') && <HousekeepingDashboard userRole={userRole} currentUser={currentUser} initialView={focus?.view} />}
                {activeTab === 'fb' && allowed('fb') && <FbCovers />}
                {activeTab === 'bar' && allowed('bar') && <BarTab userRole={userRole} />}
                {activeTab === 'finance' && allowed('finance') && <FinanceDashboard userRole={userRole} initialTab={focus?.finance_tab} initialQuery={focus?.q} />}
                {activeTab === 'inbox' && allowed('inbox') && <ContactInbox userRole={userRole} />}
                {activeTab === 'bi' && allowed('bi') && <OwnerBI userRole={userRole} />}
                {activeTab === 'stats' && allowed('stats') && <ReservationStats />}
                {activeTab === 'rates' && allowed('rates') && <RatesAvailability userRole={userRole} />}
                {activeTab === 'coupons' && allowed('coupons') && <CouponsManager userRole={userRole} />}
                {activeTab === 'inroom' && allowed('inroom') && <InRoomManager currentUser={currentUser} />}
                {activeTab === 'roomhistory' && allowed('roomhistory') && <RoomHistory />}
                {activeTab === 'arrivals' && allowed('arrivals') && <ArrivalDesk userRole={userRole} onNavigate={handleSearchNavigate} />}
                {activeTab === 'emails' && allowed('emails') && <GuestEmailsManager userRole={userRole} />}
                {activeTab === 'bookings' && allowed('bookings') && <BookingsManager userRole={userRole} />}
                {activeTab === 'rooms' && allowed('rooms') && <RoomsManager userRole={userRole} />}
                {activeTab === 'gallery' && allowed('gallery') && <GalleryManager userRole={userRole} />}
                {activeTab === 'testimonials' && allowed('testimonials') && <TestimonialsManager userRole={userRole} />}
                {activeTab === 'trust' && allowed('trust') && <TrustTab userRole={userRole} onOpenTab={setActiveTab} />}
                {activeTab === 'translations' && allowed('translations') && <TranslationsManager onOpenTab={setActiveTab} />}
                {activeTab === 'conversations' && allowed('conversations') && <ConversationsManager userRole={userRole} />}
                {activeTab === 'users' && allowed('users') && <UserManagement currentUser={currentUser} />}
              </>;
            })()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
